/**
 * O que a tela diz de cada arquivo enviado (E107.F1, ADR-0087; pelo recebimento desde a E133.F2,
 * ADR-0089).
 *
 * Dois lugares enviam — o passo de arquivos (cadastro e abertura) e o "Adicionar documentos" da mesa
 * eletrônica — e os dois dizem a mesma coisa: quantos documentos entraram, o que ficou guardado sem
 * exame e por quê, e o que já estava no caso. O pacote que não abre deixou de ser recusado: fica
 * guardado, com o motivo.
 */
import { ptBR } from '@/i18n/pt-BR';
import { separacaoDoEnvio, type SourceDocumentRecordData } from '@/lib/case_source_documents';
import type { EntradaNoRecebimentoData, MotivoNaoExaminado } from '@/lib/recebimentos';

const t = ptBR.exameEletronico.adicionarDocumentos;

/** O que o seletor do caso eletrônico oferece: os documentos, o pacote, as duas formas de evidência
 * do banco e os registros da jornada que o exame lê (E156.F1). O servidor decide pelos bytes.
 * E209.F2.T04 (GUS-296 item 5h): sem `.xls`, que não tem leitor (decisão de 01/11); os textos da
 * área de soltar dizem exatamente esta lista. */
export const ACCEPT_ELETRONICA = '.pdf,.docx,.jpg,.jpeg,.png,.zip,.json,.tst,.xlsx,.csv,.txt,.log';

export function textoDoMotivo(motivo: MotivoNaoExaminado): string {
  return t.motivoNaoExaminado[motivo];
}

/** O envio que abriu um pacote (ou que repetiu um pacote inteiro já recebido). */
export function eEntradaDePacote(entrada: EntradaNoRecebimentoData): boolean {
  return entrada.pacote_document_id !== null;
}

/** "{nome}: N arquivos adicionados ao caso.", o que ficou guardado sem exame e (E111.F1) o que já
 * estava no caso com os mesmos bytes. */
export function textoDoPacote(nome: string, entrada: EntradaNoRecebimentoData): string {
  const n = entrada.documentos.length;
  const partes = [
    (n === 1 ? t.pacoteRecebidoUm : t.pacoteRecebido)
      .replace('{nome}', nome)
      .replace('{n}', String(n)),
  ];
  if (entrada.nao_examinados.length > 0) {
    const lista = entrada.nao_examinados
      .map((i) => `${i.nome} (${textoDoMotivo(i.motivo)})`)
      .join('; ');
    partes.push(t.pacoteNaoExaminados.replace('{lista}', lista));
  }
  if (entrada.copias_identicas.length > 0) {
    const lista = entrada.copias_identicas.map((c) => c.caminho).join('; ');
    partes.push(t.pacoteCopias.replace('{lista}', lista));
  }
  return partes.join(' ');
}

/** E111.F1: o envio avulso cujos bytes já estavam no caso — declarado, nunca calado. */
export function textoDaCopiaIdentica(nome: string): string {
  return t.copiaIdentica.replace('{nome}', nome);
}

/** E216.F7 (GUS-317 item 9): nada novo entrou — o arquivo, ou o pacote inteiro, tem os bytes de um
 * que já está no caso (documento ou guardado sem exame). A linha diz "já recebido". */
export function jaRecebido(entrada: EntradaNoRecebimentoData): boolean {
  const { documentos, nao_examinados: fora, copias_identicas: copias } = entrada;
  const foraJaEstava = fora.every((item) => item.copia_identica === true);
  return documentos.length === 0 && foraJaEstava && copias.length + fora.length > 0;
}

/** O arquivo solto que o exame não lê: recebido e guardado, com o motivo. */
export function textoDoNaoExaminado(nome: string, motivo: MotivoNaoExaminado): string {
  return t.naoExaminado.replace('{nome}', nome).replace('{motivo}', textoDoMotivo(motivo));
}

/** E97.F4.T03: o arquivo único diz na hora se foi separado, e em quantos documentos. */
export function textoDaSeparacao(
  nome: string,
  separacao: { documentos: number } | { motivos: string },
): string {
  if ('documentos' in separacao) {
    return t.arquivoSeparado.replace('{nome}', nome).replace('{n}', String(separacao.documentos));
  }
  return t.arquivoNaoSeparado.replace('{nome}', nome).replace('{motivos}', separacao.motivos);
}

const TEXTO_DO_KIND: Partial<Record<SourceDocumentRecordData['kind'], string>> = {
  protocolo: t.comprovanteReconhecido,
  evidencia: t.evidenciaRecebida,
};

/** O que entrou: o pacote, a cópia idêntica, o guardado sem exame ou o documento (peça,
 * comprovante, evidência, arquivo único separado). */
export function textoDaEntrada(nome: string, entrada: EntradaNoRecebimentoData): string {
  if (jaRecebido(entrada)) return textoDaCopiaIdentica(nome);
  if (eEntradaDePacote(entrada)) return textoDoPacote(nome, entrada);
  if (entrada.copias_identicas.length > 0) return textoDaCopiaIdentica(nome);
  const [documento] = entrada.documentos;
  const [fora] = entrada.nao_examinados;
  if (documento === undefined) {
    return fora === undefined
      ? t.enviado.replace('{nome}', nome)
      : textoDoNaoExaminado(nome, fora.motivo);
  }
  const separacao = separacaoDoEnvio(documento);
  if (separacao !== null) return textoDaSeparacao(nome, separacao);
  return (TEXTO_DO_KIND[documento.kind] ?? t.enviado).replace('{nome}', nome);
}
